/**
 * 法务页（隐私政策、使用条款）。
 *
 * 内容力求如实：本站确实没有后端、没有第三方统计、也不采集访客数据，
 * 所以条款只写真实存在的行为，不抄一段用不上的模板。
 */
import type { Block } from './blocks';

export interface LegalSection {
  id: string;
  title: string;
  blocks: readonly Block[];
}

export interface LegalDoc {
  slug: 'privacy' | 'terms';
  title: string;
  updated: string;
  intro: string;
  sections: readonly LegalSection[];
}

const NOT_COLLECTED: readonly string[] = [
  '不进行第三方统计与分析（既无埋点脚本，也无访问统计服务）。',
  '不设置用于追踪的 Cookie，亦无登录态 Cookie —— 本站没有账号体系。',
  '不采集浏览行为、设备指纹或位置信息。',
  '不投放广告，不构建用户画像，也不向第三方提供任何访客数据。',
];

const LOCAL_STORAGE: readonly Block[] = [
  {
    kind: 'p',
    text: '本站仅在您的设备上写入一项数据：`havorix.theme`，用于记住您选择的浅色或深色模式。它存放于浏览器的 `localStorage` 中，取值仅有 `light` 与 `dark`。',
  },
  {
    kind: 'p',
    text: '该项数据不会离开您的浏览器，也不会用于识别您的身份。清除浏览器数据即可删除。',
  },
];

const FORMS: readonly Block[] = [
  {
    kind: 'p',
    text: '申请试用与联系表单均为**纯前端**实现：您填写的内容仅保存在当前页面的内存中。点击「生成邮件」后，浏览器在本地拼装一封邮件并交由您的邮箱客户端，或由您一键复制正文。',
  },
  {
    kind: 'ul',
    items: [
      '不存在提交接口，内容不会被发送至我们的服务器。',
      '刷新或关闭页面即丢失，本站不保存任何表单内容。',
      '若您通过邮箱客户端将邮件发送给我们，则回归常规邮件往来：仅用于回复本次沟通，不建立邮件列表、不发送营销邮件、不提供给第三方。',
    ],
  },
];

const HOSTING: readonly Block[] = [
  {
    kind: 'p',
    text: '本站为静态站点，托管于 Cloudflare 网络。与任何托管服务一致，为将页面送达您的浏览器并抵御攻击，服务商需处理必要的请求信息（如 IP 地址、请求路径、浏览器标识）。这属于托管层的常规行为，受服务商隐私政策约束。',
  },
  {
    kind: 'p',
    text: '我们不会提取这些信息用于构建访客档案，也不会分享给第三方。',
  },
];

const PRIVACY_RIGHTS: readonly Block[] = [
  {
    kind: 'p',
    text: '由于我们并不收集访客数据，因此通常不存在可供导出的个人数据。若您曾通过邮件与我们联系，并希望删除该邮件及其中的个人信息，来信说明即可，我们将在确认后删除。',
  },
  {
    kind: 'p',
    text: '对隐私政策本身有疑问，同样可发送至 contact@havorix.cn。',
  },
];

export const PRIVACY: LegalDoc = {
  slug: 'privacy',
  title: '隐私政策',
  updated: '2026-09-25',
  intro: '先说明结论：本站没有后端，不收集访客数据，也不使用任何第三方统计服务。以下逐项说明我们的具体做法。',
  sections: [
    { id: 'what-we-collect', title: '我们不收集哪些数据', blocks: [{ kind: 'ul', items: NOT_COLLECTED }] },
    { id: 'local-storage', title: '存储在您设备上的数据', blocks: LOCAL_STORAGE },
    { id: 'forms', title: '表单与邮件', blocks: FORMS },
    { id: 'hosting', title: '托管服务与请求日志', blocks: HOSTING },
    { id: 'platform', title: '平台产品中的数据处理', blocks: [
      {
        kind: 'p',
        text: '本节说明的是**平台产品**的数据处理方式，与本站的访问行为无关。',
      },
      {
        kind: 'ul',
        items: [
          '平台保存账号、角色权限、设备档案、平台设置与操作日志（默认保留 180 天）。',
          '实时遥测由浏览器直连机体获取，不经过平台，因此平台不持有遥测数据。',
          '平台**不保存历史航迹**：地图上的轨迹是页面打开期间逐步累积的结果，关闭页面即消失。',
          '接入凭据加密存储，仅在向机体建立连接时按需解密。',
        ],
      },
      {
        kind: 'p',
        text: '若您使用的是部署于自有账号下的私有化版本，上述数据的实际控制者为贵方机构，我们不经手。',
      },
    ] },
    { id: 'rights', title: '您可主张的权利与联系方式', blocks: PRIVACY_RIGHTS },
  ],
};

const ACCEPTABLE_USE: readonly string[] = [
  '请勿尝试破坏、过载或以非预期方式访问本站。',
  '请勿使用自动化脚本大批量抓取内容后重新发布。',
  '引用本站内容时，请保留出处与 Havorix™ 品牌标识。',
];

const OPERATOR_RESPONSIBILITY: readonly Block[] = [
  {
    kind: 'p',
    text: '平台是**监管与操作界面**，并非飞行控制系统，也不替代机体自身的飞控逻辑与安全机制。飞行安全、空域申请、作业资质与保险，均由运营方自行负责。',
  },
  {
    kind: 'ul',
    items: [
      '平台下发的指令最终由机体执行，机体侧应实现必要的状态校验与拒绝逻辑。',
      '接入凭据由接入方自行保管；因凭据泄露导致的问题不在平台可控范围内。',
      '接入文档为公开内容，任何符合约定的机体均可接入，我们不对机体本身的质量作出担保。',
    ],
  },
];

const AVAILABILITY: readonly Block[] = [
  {
    kind: 'p',
    text: '本站为静态站点，平台为独立部署的服务。我们会尽力维持可用性，但不承诺任何具体的可用率数字 —— 页面上不呈现缺乏依据的承诺。',
  },
  {
    kind: 'p',
    text: '本站内容可能随时调整，包括功能说明与方案范围。已签署协议所涉内容，以双方书面约定为准。',
  },
];

export const TERMS: LegalDoc = {
  slug: 'terms',
  title: '使用条款',
  updated: '2026-09-25',
  intro: '本页说明使用 havorix.cn 与 Havorix™ 平台服务时的基本约定。具体合作项目以双方签署的书面协议为准。',
  sections: [
    {
      id: 'site',
      title: '本站内容',
      blocks: [
        {
          kind: 'p',
          text: '本站用于介绍 Havorix™ 的品牌、平台能力与方案。站内文字与插图可能随时调整；实际可用的能力范围以交付贵方的版本为准。',
        },
        {
          kind: 'p',
          text: 'Havorix™ 是商标注册申请中的标识。在注册完成之前，我们统一使用 ™ 标记。',
        },
      ],
    },
    { id: 'acceptable-use', title: '使用规范', blocks: [{ kind: 'ul', items: ACCEPTABLE_USE }] },
    { id: 'no-hardware-sales', title: '关于产品范围', blocks: [
      {
        kind: 'p',
        text: '我们不销售无人机整机。本站与平台均不提供整机购买渠道，也没有商城或支付功能。平台提供的是监管软件、部署与定制服务。',
      },
    ] },
    { id: 'operator', title: '接入方与运营方的责任划分', blocks: OPERATOR_RESPONSIBILITY },
    { id: 'availability', title: '可用性与变更', blocks: AVAILABILITY },
    { id: 'liability', title: '责任限制', blocks: [
      {
        kind: 'p',
        text: '在适用法律允许的范围内，对于因使用或无法使用本站、或因依赖站内描述而产生的间接损失，我们不承担责任。',
      },
      {
        kind: 'p',
        text: '平台服务本身的责任划分以双方签署的服务协议为准；本页不构成对该协议的替代或补充。',
      },
    ] },
    { id: 'contact', title: '联系方式', blocks: [
      {
        kind: 'p',
        text: '如对条款有疑问，或需确认某项具体约定，请发送邮件至 contact@havorix.cn。',
      },
    ] },
  ],
};

export const LEGAL_DOCS: readonly LegalDoc[] = [PRIVACY, TERMS];

export function legalDoc(slug: string): LegalDoc | undefined {
  return LEGAL_DOCS.find((doc) => doc.slug === slug);
}
